<template>
  <div>
    <navigation></navigation>

    <premium-section v-if="userLoaded" feature="/analysis/progress">
      <div style="height: calc(100vh - 72px); margin-bottom: -24px; display: flex;flex-direction: column;">
        <v-progress-linear v-if="! loading.complete" :model-value="loading.counter.toLoad && loading.counter.loaded ? loading.counter.loaded / loading.counter.toLoad * 100 : 2"
                           color="accent" rounded>
        </v-progress-linear>

        <dashboards-filters v-if="loading.complete" :filters="['Projects', 'Users', 'Dates', 'Display']" class="flex-grow-0">
          <div style="flex: 0 1 12px"></div>
          <columns-filter :cols="cols"></columns-filter>
          <template #append>
            <v-spacer style="flex-grow: 2; min-width: 8px"></v-spacer>
            <dashboards-filters-export :exporting="exporting" pdf @pdf-export="pdfExport"></dashboards-filters-export>
          </template>
        </dashboards-filters>

        <div v-show="loading.complete" id="progress-dashboard-cols-wrapper" class="flex-grow-1" style="display: flex; overflow-x: hidden">
          <div class="d-flex flex-grow-1" style="overflow-x: auto; overflow-y: hidden;">
            <progress-dashboard-col v-for="col in filteredCols" :key="col.id" :col="col" :col-elements="planningElementsByCol[col.id]"
                                    @save-el="saveEl">
            </progress-dashboard-col>
          </div>
        </div>

        <!-- MODALS -->
        <dashboards-element-modal></dashboards-element-modal>
      </div>
    </premium-section>
  </div>
</template>

<script>
  import { mapState, mapActions } from 'pinia';
  import useUiDashboardsStore from '@/stores/ui/dashboards';
  import useUsersStore from '@/stores/users';
  import useUsersAccessRightStore from '@/stores/users/accessRight';
  import useMultiprojectsStore from '@/stores/multiprojects/multiprojects';
  import Planning from '@/models/Planning';
  import Navigation from '@/components/Navigation/Navigation.vue';
  import DashboardsElementModal from '../partials/DashboardsElementModal.vue';
  import DashboardsFilters from '../DashboardsFilters/DashboardsFilters.vue';
  import DashboardsFiltersExport from '../DashboardsFilters/DashboardsFiltersExport.vue';
  import ColumnsFilter from '../DashboardsFilters/ColumnsFilter.vue';
  import ProgressDashboardCol from './ProgressDashboardCol.vue';

  export default {
    components: {
      Navigation,
      DashboardsElementModal,
      DashboardsFilters,
      DashboardsFiltersExport,
      ColumnsFilter,
      ProgressDashboardCol,
    },
    data() {
      return {
        cols: [
          { id: 'finished', label: this.$t('MONITORING_PROGRESS.FINISHED'), color: 'successgreen', icon: 'far fa-check-circle', orderBy: '-data.endtime' },
          { id: 'inprogress', label: this.$t('MONITORING_PROGRESS.IN_PROGRESS'), color: 'primary', icon: 'far fa-hourglass', orderBy: 'data.endtime' },
          { id: 'overdue', label: this.$t('MONITORING_PROGRESS.OVERDUE'), color: 'errorred', icon: 'far fa-exclamation-triangle', orderBy: '-data.endtime' },
          { id: 'future', label: this.$t('MONITORING_PROGRESS.FUTURE'), color: 'midgrey', icon: 'far fa-rocket', orderBy: 'data.starttime' },
        ],
        exporting: { inprogress: false, success: false, error: false },
        usersStore: useUsersStore(),
        usersAccessRightStore: useUsersAccessRightStore(),
        multiprojectsStore: useMultiprojectsStore(),
      };
    },
    computed: {
      filteredCols() {
        return this.cols.filter(col => this.progressOptions.selectedCols[col.id]);
      },
      allPlanningElements() {
        // selected plannings -> elements
        const elements = [];
        const selectedProjectsSet = new Set(this.selectedProjects);
        this.projects.forEach((projectData) => {
          if (! projectData.elements || ! selectedProjectsSet.has(projectData.id)) return;
          projectData.elements.forEach((el) => {
            if (! el.hasDates()) return;
            elements.push(el);
          });
        });
        return elements;
      },
      allSelectedUsers() {
        const allSelectedUsers = new Set();
        this.companyUsers.forEach((user) => {
          if (this.selectedCompanyUsers.includes(user.id)) allSelectedUsers.add(user.id);
        });
        this.deletedUsers.forEach((user) => {
          if (this.selectedDeletedUsers.includes(user.id)) allSelectedUsers.add(user.id);
        });
        this.virtualParticipants.forEach((user) => {
          if (this.selectedVirtualParticipants.includes(user.username)) allSelectedUsers.add(`vp${user.username}`);
        });
        return allSelectedUsers;
      },
      planning() {
        const planning = new Planning();
        if (! this.loading.complete || ! this.projects.length) return planning;
        planning.elements = this.allPlanningElements.filter((el) => {
          const elUsers = el.getUsers();
          if (elUsers && elUsers.some(user => ! user.group_id)) { // exclude groups from considered users
            if (! elUsers.find(user => this.allSelectedUsers.has(user.id || `vp${user.username}`))) return false;
          } else if (! this.filterOptions.unassigned) {
            return false;
          }
          if (this.filterOptions.el_type && this.filterOptions.el_type != 'both' && ! el.isType(this.filterOptions.el_type)) return false;
          if (this.selectedDates.starttime && el.getEndTime().isBefore(this.selectedDates.starttime)) return false;
          if (this.selectedDates.endtime && el.getStartTime().startOf('day').isAfter(this.selectedDates.endtime)) return false;
          return true;
        });
        planning.config.setDesign();
        return planning;
      },
      planningElementsByCol() {
        const elementsByCol = {};
        this.planning.elements.forEach((el) => {
          let colId;
          if (el.getProgress() == 100) {
            colId = 'finished';
          } else if (el.getEndTime()?.isBefore()) {
            colId = 'overdue';
          } else {
            colId = el.getStartTime()?.isAfter() ? 'future' : 'inprogress';
          }
          if (! elementsByCol[colId]) elementsByCol[colId] = [];
          elementsByCol[colId].push(el);
        });
        return elementsByCol;
      },
      userLoaded() { return this.usersStore.user.id > 0; },
      ...mapState(useMultiprojectsStore, ['projects', 'selectedProjects', 'companyUsers', 'selectedCompanyUsers', 'virtualParticipants',
                                          'selectedVirtualParticipants', 'deletedUsers', 'selectedDeletedUsers',
                                          'loading', 'selectedDates', 'selectedDisplay', 'filterOptions', 'progressOptions']),
    },
    created() {
      this.multiprojectsStore.loadFilters({
        dashboardName: this.$route.path.slice(1).replace(/\//g, '_'),
        selectedDisplay: {
          description: false, progress: true, checklist: true, users: true, links: false, budgets: false,
        },
        filterOptions: { el_type: 'task' },
      });
      this.multiprojectsStore.load();
    },
    methods: {
      pdfExport() {
        _.extend(this.exporting, { inprogress: true, success: false, error: false });
        const $html = document.querySelector("html").cloneNode(true);
        const queryEl = $html.querySelector('#progress-dashboard-cols-wrapper');
        if (! queryEl) {
          this.exporting.inprogress = false;
          return;
        }
        Array.from(queryEl.children).forEach(el => el.classList.add('px-4', 'mx-n4')); // space for inner elements box shadow
        $html.querySelector("body").innerHTML = `<div style="padding: 0 20px;">${queryEl.innerHTML}</div>`;
        $html.querySelector('body').classList.add('v-application');
        $html.querySelectorAll(".progress-dashboard-col").forEach(el => el.style.setProperty('width', 'calc(25% - 15px)'));
        $html.querySelectorAll(".warningorange").forEach(el => el.style.setProperty('background', '#ff9b1d'));
        $html.querySelectorAll(".successgreen").forEach(el => el.style.setProperty('background', '#00b100'));

        window.apiSrv.call('pdf', 'store', { html: $html.innerHTML, orientation: "landscape", footer: this.$t('MONITORING_PROGRESS.TITLE') }).then((response) => {
          if (response && response.data && response.data.pdfurl) {
            this.exporting.inprogress = false;
            this.exporting.success = true;
            setTimeout(() => { this.exporting.success = false; }, 3000);
            window.open(`${response.data.pdfurl}/${this.$t('MONITORING_PROGRESS.TITLE')}.pdf`, "_blank");
          }
        }).catch((message) => {
          this.exporting.inprogress = false;
          this.exporting.error = message || "Error : not exported";
        });
      },
      ...mapActions(useUiDashboardsStore, ['saveEl']),
    },
  };
</script>
